import { slashFactory, SlashProvider } from '@milkdown/kit/plugin/slash';
import { wrapInList } from '@milkdown/kit/prose/schema-list';
import type { Ctx } from '@milkdown/kit/ctx';
import {
    usePluginViewContext,
    usePluginViewFactory
} from '@prosemirror-adapter/react';
import {
    IconArrowsHorizontal,
    IconBlockquote,
    IconCode,
    IconFileBroken,
    IconH1,
    IconH2,
    IconH3,
    IconLayoutBottombar,
    IconList,
    IconListNumbers,
    IconMinus,
    IconShieldCheck,
    IconSitemap,
    IconTable,
    IconTimeline,
    IconUsers
} from '@tabler/icons-react';

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { ActionIcon, Group, Paper, ScrollArea, Stack, Text, UnstyledButton } from '@mantine/core';

import { getAllBlockPlugins } from '../core/BlockPluginRegistry';
import { useEditorAdapter } from '../core/EditorAdapterContext';
import type { EditorAdapter } from '../core/types';

import './slash-menu.css';

// ── Slash command item definition ──

export interface SlashItem {
    key: string;
    label: string;
    icon: ReactNode;
    group: string;
    onRun: () => void;
}

// Icons for known block plugin names
const blockPluginIcons: Record<string, ReactNode> = {
    mermaid: <IconSitemap size={18} />,
    timeline: <IconTimeline size={18} />,
    userStories: <IconUsers size={18} />,
    riskRegister: <IconShieldCheck size={18} />,
    newPage: <IconFileBroken size={18} />,
    sectionPortrait: <IconLayoutBottombar size={18} />,
    sectionLandscape: <IconArrowsHorizontal size={18} />
};

// Extra items injected from outside (set via useSlashMenu)
let extraSlashItems: SlashItem[] = [];

export function setExtraSlashItems(items: SlashItem[]) {
    extraSlashItems = items;
}

function buildSlashItems(adapter: EditorAdapter): SlashItem[] {
    const items: SlashItem[] = [];

    // ── Text blocks ──
    items.push(
        { key: 'h1', label: 'Heading 1', icon: <IconH1 size={18} />, group: 'Text', onRun: () => adapter.applyBlockType('h1') },
        { key: 'h2', label: 'Heading 2', icon: <IconH2 size={18} />, group: 'Text', onRun: () => adapter.applyBlockType('h2') },
        { key: 'h3', label: 'Heading 3', icon: <IconH3 size={18} />, group: 'Text', onRun: () => adapter.applyBlockType('h3') },
        { key: 'quote', label: 'Blockquote', icon: <IconBlockquote size={18} />, group: 'Text', onRun: () => adapter.applyBlockType('quote') },
        { key: 'code', label: 'Code Block', icon: <IconCode size={18} />, group: 'Text', onRun: () => adapter.applyBlockType('code') },
        { key: 'hr', label: 'Horizontal Rule', icon: <IconMinus size={18} />, group: 'Text', onRun: () => adapter.insertMarkdown('\n\n---\n\n') }
    );

    // ── Lists ──
    const wrapList = (listType: 'bullet_list' | 'ordered_list') => {
        (adapter as unknown as { runProseMirrorCommand: (fn: (view: import('@milkdown/kit/prose/view').EditorView) => void) => void })
            .runProseMirrorCommand((view) => {
                const nodeType = view.state.schema.nodes[listType];
                if (nodeType) wrapInList(nodeType)(view.state, view.dispatch);
                view.focus();
            });
    };

    items.push(
        { key: 'bullet', label: 'Bullet List', icon: <IconList size={18} />, group: 'Lists', onRun: () => wrapList('bullet_list') },
        { key: 'ordered', label: 'Ordered List', icon: <IconListNumbers size={18} />, group: 'Lists', onRun: () => wrapList('ordered_list') }
    );

    // ── Insert ──
    items.push({
        key: 'table',
        label: 'Table',
        icon: <IconTable size={18} />,
        group: 'Insert',
        onRun: () => adapter.insertMarkdown(
            '\n\n| Header 1 | Header 2 | Header 3 |\n| --- | --- | --- |\n| Cell 1 | Cell 2 | Cell 3 |\n\n'
        )
    });

    // ── Custom blocks from registry ──
    for (const plugin of getAllBlockPlugins()) {
        if (!plugin.toolbarInsert) continue;

        const icon = blockPluginIcons[plugin.name] ?? plugin.toolbarInsert.icon;
        const group = plugin.toolbarInsert.category === 'break' ? 'Breaks' : 'Components';

        items.push({
            key: plugin.name,
            label: plugin.toolbarInsert.title,
            icon,
            group,
            onRun: () => {
                if (plugin.toolbarInsert!.customInsert) {
                    plugin.toolbarInsert!.customInsert(adapter);
                } else {
                    const defaultData: unknown =
                        typeof plugin.defaultData === 'function'
                            ? (plugin.defaultData as () => unknown)()
                            : plugin.defaultData;
                    adapter.insertBlock(plugin.name, defaultData);
                }
            }
        });
    }

    // ── Extra items (cost estimate, BPMN, etc.) ──
    items.push(...extraSlashItems);

    return items;
}

// ── Slash plugin ──

export const slashMenu = slashFactory('firedocs');

/**
 * React component for the slash menu dropdown.
 */
function SlashMenuView() {
    const { view, prevState } = usePluginViewContext();
    const adapter = useEditorAdapter();
    const menuRef = useRef<HTMLDivElement>(null);
    const providerRef = useRef<SlashProvider | null>(null);
    const [filter, setFilter] = useState('');
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [visible, setVisible] = useState(false);

    // Rebuilt every render — cheap array construction, and ensures
    // module-level extraSlashItems are always up to date.
    const items = buildSlashItems(adapter);

    const filteredItems = useMemo(() => {
        if (!filter) return items;
        const lc = filter.toLowerCase();
        return items.filter(
            (item) =>
                item.label.toLowerCase().includes(lc) ||
                item.key.toLowerCase().includes(lc)
        );
    }, [items, filter]);

    // Group items for display
    const groups = useMemo(() => {
        const map = new Map<string, SlashItem[]>();
        for (const item of filteredItems) {
            const list = map.get(item.group) ?? [];
            list.push(item);
            map.set(item.group, list);
        }
        return map;
    }, [filteredItems]);

    useEffect(() => {
        const el = menuRef.current;
        if (!el) return;

        const provider = new SlashProvider({
            content: el,
            debounce: 50,
            trigger: '/',
            shouldShow: (v) => {
                const { selection } = v.state;
                if (!selection.empty) return false;
                const { $from } = selection;
                if ($from.parent.type.name !== 'paragraph') return false;
                const text = $from.parent.textContent;
                return text.startsWith('/');
            }
        });

        provider.onShow = () => setVisible(true);
        provider.onHide = () => {
            setVisible(false);
            setFilter('');
            setSelectedIndex(0);
        };

        providerRef.current = provider;
        return () => {
            provider.destroy();
            providerRef.current = null;
        };
    }, []);

    // Update provider + extract filter text on every state change
    useEffect(() => {
        providerRef.current?.update(view, prevState);

        if (visible) {
            const content = providerRef.current?.getContent(view);
            const text = content?.startsWith('/') ? content.slice(1) : (content ?? '');
            setFilter(text);
            setSelectedIndex(0);
        }
    });

    const executeItem = useCallback(
        (item: SlashItem) => {
            // Delete the slash text before executing
            const { state, dispatch } = view;
            const { $from } = state.selection;
            const start = $from.start();
            dispatch(state.tr.delete(start, $from.pos));

            item.onRun();
            providerRef.current?.hide();
        },
        [view]
    );

    // Keyboard navigation
    useEffect(() => {
        if (!visible) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            const flatItems = filteredItems;
            if (!flatItems.length) return;

            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSelectedIndex((i) => (i + 1) % flatItems.length);
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSelectedIndex((i) => (i - 1 + flatItems.length) % flatItems.length);
            } else if (e.key === 'Enter') {
                e.preventDefault();
                const item = flatItems[selectedIndex];
                if (item) executeItem(item);
            } else if (e.key === 'Escape') {
                e.preventDefault();
                providerRef.current?.hide();
            }
        };

        document.addEventListener('keydown', handleKeyDown, { capture: true });
        return () => document.removeEventListener('keydown', handleKeyDown, { capture: true });
    }, [visible, filteredItems, selectedIndex, executeItem]);

    let flatIndex = 0;

    return (
        <div ref={menuRef} className="milkdown-slash-menu">
            {visible && filteredItems.length > 0 && (
                <Paper shadow="md" withBorder radius="sm" p={4}>
                    <ScrollArea.Autosize mah={300}>
                        <Stack gap={2}>
                            {Array.from(groups.entries()).map(([group, groupItems]) => (
                                <div key={group}>
                                    <Text size="xs" fw={600} c="dimmed" px="xs" py={2}>
                                        {group}
                                    </Text>
                                    {groupItems.map((item) => {
                                        const idx = flatIndex++;
                                        return (
                                            <UnstyledButton
                                                key={item.key}
                                                className="milkdown-slash-item"
                                                data-selected={idx === selectedIndex || undefined}
                                                onMouseEnter={() => setSelectedIndex(idx)}
                                                onClick={() => executeItem(item)}
                                            >
                                                <Group gap="xs" wrap="nowrap">
                                                    <ActionIcon
                                                        variant="light"
                                                        color="gray"
                                                        size="sm"
                                                        component="span"
                                                        tabIndex={-1}
                                                    >
                                                        {item.icon}
                                                    </ActionIcon>
                                                    <Text size="sm">{item.label}</Text>
                                                </Group>
                                            </UnstyledButton>
                                        );
                                    })}
                                </div>
                            ))}
                        </Stack>
                    </ScrollArea.Autosize>
                </Paper>
            )}
            {visible && filteredItems.length === 0 && (
                <Paper shadow="md" withBorder radius="sm" p="sm">
                    <Text size="sm" c="dimmed">No matching commands</Text>
                </Paper>
            )}
        </div>
    );
}

/**
 * Call from MilkdownEditorInner to wire the slash menu plugin.
 * Must be called inside ProsemirrorAdapterProvider.
 */
export function useSlashMenu() {
    const pluginViewFactory = usePluginViewFactory();

    return (ctx: Ctx) => {
        ctx.set(slashMenu.key, {
            view: pluginViewFactory({ component: SlashMenuView })
        });
    };
}
